import React from 'react';
import { Controller } from 'react-hook-form';

/**
 * ShipChTab — SHIPPING & CH DETAILS tab
 * Layout:
 *  - Row 1: Shipping Bill (5:7) + CFS Name (fills rest)
 *  - CH Expenses: 3-column grid, each column has label on top and input below
 *    paired as: [per cont. input | auto-total readonly]
 *  - Calculated Totals: uniform 5:7 label:input horizontal layout
 */
const ShipChTab = ({ control, watch, errors }) => {
  const {
    cfsCharges,
    chaSurvey,
    examinationCharge,
    cfsGroundRent,
    freightCharge,
    unLoading,
    extraExpenses,
    sec49,
    fumigationCharge,
    totalCft,
    totalInt,
    od,
    agents,
    yardRent,
    cost,
    paymentOption,
  } = watch();

  const isCash = String(paymentOption || 'Credit').toLowerCase() === 'cash';

  return (
    <div className="w-100">
      <h5 className="de-section-title">SHIPPING BILL</h5>

      {/* Row 1: SHIPPING BILL + CFS NAME */}
      <div className="row g-3">
        <div className="col-12 col-lg-4">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start">SHIPPING BILL :</label>
            <div className="col-7">
              <Controller
                name="shippingBill"
                control={control}
                render={({ field }) => (
                  <input {...field} type="number" className="form-control" placeholder="Shipping Bill" />
                )}
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-8">
          <div className="row align-items-center g-2">
            <label className="col-form-label de-label text-start de-label-span-2">CFS NAME :</label>
            <div className="de-input-span-2">
              <Controller
                name="cfsName"
                control={control}
                render={({ field }) => (
                  <input {...field} type="text" className="form-control" placeholder="CFS Name" />
                )}
              />
            </div>
          </div>
        </div>
      </div>

      {/* CH & Other Expenses — 3 columns, label on top, per-cont above auto-total */}
      <h6 className="de-subsection-title mt-4">CH &amp; Other Expenses</h6>
      <div className="de-form-grid de-cols-3">

        {/* CFS CHARGES */}
        <div className="de-field-group">
          <div className="d-flex align-items-center gap-2 mb-1">
            <label className="de-label mb-0">CFS CHARGES</label>
            <Controller
              name="cfsChargesNew"
              control={control}
              render={({ field }) => (
                <input {...field} type="number" className="form-control" placeholder="per cont." />
              )}
            />
          </div>
          <input type="number" className="form-control de-readonly-field" value={cfsCharges || ''} readOnly />
        </div>

        {/* CHA & SURVEY */}
        <div className="de-field-group">
          <div className="d-flex align-items-center gap-2 mb-1">
            <label className="de-label mb-0">CHA &amp; SURVEY</label>
            <Controller
              name="chaSurveyNew"
              control={control}
              render={({ field }) => (
                <input {...field} type="number" className="form-control" placeholder="per cont." />
              )}
            />
          </div>
          <input type="number" className="form-control de-readonly-field" value={chaSurvey || ''} readOnly />
        </div>

        {/* EXAMINATION CHARGES */}
        <div className="de-field-group">
          <div className="d-flex align-items-center gap-2 mb-1">
            <label className="de-label mb-0">EXAMINATION CHARGES :</label>
            <Controller
              name="examinationChargenew"
              control={control}
              render={({ field }) => (
                <input {...field} type="number" className="form-control" placeholder="per cont." />
              )}
            />
          </div>
          <input type="number" className="form-control de-readonly-field" value={examinationCharge || ''} readOnly />
        </div>

        {/* CFS GROUND RENT */}
        <div className="de-field-group">
          <div className="d-flex align-items-center gap-2 mb-1">
            <label className="de-label mb-0">CFS GROUND RENT(1500) :</label>
            <Controller
              name="cfsGroundRentNew"
              control={control}
              render={({ field }) => (
                <input {...field} type="number" className="form-control" placeholder="per cont." />
              )}
            />
          </div>
          <input type="number" className="form-control de-readonly-field" value={cfsGroundRent || ''} readOnly />
        </div>

        {/* FREIGHT CHARGE */}
        <div className="de-field-group">
          <div className="d-flex align-items-center gap-2 mb-1">
            <label className="de-label mb-0">FREIGHT CHARGE:</label>
            <Controller
              name="freightChargeNew"
              control={control}
              render={({ field }) => (
                <input {...field} type="number" className="form-control" placeholder="per cont." />
              )}
            />
          </div>
          <input type="number" className="form-control de-readonly-field" value={freightCharge || ''} readOnly />
        </div>

        {/* UNLOADING */}
        <div className="de-field-group">
          <div className="d-flex align-items-center gap-2 mb-1">
            <label className="de-label mb-0">UNLOADING:</label>
            <Controller
              name="unloadingNew"
              control={control}
              render={({ field }) => (
                <input {...field} type="number" className="form-control" placeholder="per cont." />
              )}
            />
          </div>
          <input type="number" className="form-control de-readonly-field" value={unLoading || ''} readOnly />
        </div>

        {/* EXTRA EXPENSES / YARD RENT */}
        <div className="de-field-group">
          <div className="d-flex align-items-center gap-2 mb-1">
            <label className="de-label mb-0">EXTRA EXPENCES :</label>
            <Controller
              name="yardRentNew"
              control={control}
              render={({ field }) => (
                <input {...field} type="number" className="form-control" placeholder="per cont." />
              )}
            />
          </div>
          <input type="number" className="form-control de-readonly-field" value={extraExpenses || ''} readOnly />
        </div>

        {/* SEC 49 */}
        <div className="de-field-group">
          <div className="d-flex align-items-center gap-2 mb-1">
            <label className="de-label mb-0">SEC49 (5000):</label>
            <Controller
              name="sec49New"
              control={control}
              render={({ field }) => (
                <input {...field} type="number" className="form-control" placeholder="per cont." />
              )}
            />
          </div>
          <input type="number" className="form-control de-readonly-field" value={sec49 || ''} readOnly />
        </div>

        {/* FUMIGATION CHARGES */}
        <div className="de-field-group">
          <div className="d-flex align-items-center gap-2 mb-1">
            <label className="de-label mb-0">FUMIGATION CHARGES(2500):</label>
            <Controller
              name="fumigationChargeNew"
              control={control}
              render={({ field }) => (
                <input {...field} type="number" className="form-control" placeholder="per cont." />
              )}
            />
          </div>
          <input type="number" className="form-control de-readonly-field" value={fumigationCharge || ''} readOnly />
        </div>
      </div>

      <hr className="de-section-divider my-4" />

      {/* Calculated Totals & Options */}
      <div className="de-form-grid de-cols-3">
        {/* PAYMENT OPTION */}
        <div className="de-field-group">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start">PAYMENT OPTION :</label>
            <div className="col-7">
              <Controller
                name="paymentOption"
                control={control}
                render={({ field }) => (
                  <select {...field} className="form-select">
                    <option value="Cash">Cash</option>
                    <option value="Credit">Credit</option>
                  </select>
                )}
              />
            </div>
          </div>
        </div>

        {/* PER CFT COST */}
        <div className="de-field-group">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start">Per CFT Cost :</label>
            <div className="col-7">
              <input type="number" className="form-control de-readonly-field de-calc-field" value={totalCft || ''} readOnly />
            </div>
          </div>
        </div>

        {/* Show TOTAL INT, AGENTS, and Total CFT Cost ONLY when Credit */}
        {!isCash && (
          <>
            {/* TOTAL INT */}
            <div className="de-field-group">
              <div className="row align-items-center g-2">
                <label className="col-5 col-form-label de-label text-start">TOTAL INT :</label>
                <div className="col-7">
                  <input type="number" className="form-control de-readonly-field de-calc-field" value={totalInt || ''} readOnly />
                </div>
              </div>
            </div>

            {/* AGENTS */}
            <div className="de-field-group">
              <div className="row align-items-center g-2">
                <label className="col-5 col-form-label de-label text-start">AGENTS :</label>
                <div className="col-7">
                  <Controller
                    name="agents"
                    control={control}
                    render={({ field }) => (
                      <input {...field} type="number" className="form-control" placeholder="Agents" />
                    )}
                  />
                </div>
              </div>
            </div>

            {/* COST */}
            <div className="de-field-group">
              <div className="row align-items-center g-2">
                <label className="col-5 col-form-label de-label text-start">COST :</label>
                <div className="col-7">
                  <input type="number" className="form-control de-readonly-field de-calc-field" value={cost || ''} readOnly />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShipChTab;
